import React, { useContext } from "react";

import { SimpleLinkBlock, SubmenuContentBlock } from "@reactivated";

import { ReactivatedContext } from "../../context";
import { Clickable } from "../Clickables";

import styles from "./LinkList.module.scss";

export const normalizeSimpleLinks = (
    links: SimpleLinkBlock[],
): LinkListItem[] =>
    // slightly hacky/ugly, but flatMap is solving some problems that
    // filter + map + the CharBlockOrNull type struggled with when I
    // wanted to filter out links that didn't have a display name
    links.flatMap((link) =>
        link.display_name ? [{ text: link.display_name, url: link.url }] : [],
    );

export const normalizeLinks = (
    item: Extract<
        SubmenuContentBlock[number],
        { type: "link_list" | "link_list_page_tree" | "link_list_facet" }
    >,
): LinkListItem[] => {
    if (!item.value.links) return [];

    switch (item.type) {
        case "link_list":
            return normalizeSimpleLinks(item.value.links);
        case "link_list_page_tree":
        case "link_list_facet":
            return item.value.links.map((link) => ({
                text: link.title,
                url: link.url,
            }));
    }
};

type LinkListItem = {
    text: string;
    url: string | null;
};

interface Props {
    title: string | null;
    links: LinkListItem[];
    /**
     * If you pass in an ID without a title, make sure you are tagging an
     * element with the ID elsewhere so that aria-labelledby will point to
     * something. See how BackPanel does this for an example.
     */
    id: string;
}

export const LinkList = ({ title, links, id }: Props) => {
    const { request } = useContext(ReactivatedContext);

    return (
        <>
            {title && (
                <span id={id} className={styles.sectionTitle}>
                    {title}
                </span>
            )}
            <ul className={styles.linkList} aria-labelledby={id}>
                {links.map((link, index) => (
                    <li key={`${link.url}-${index}`}>
                        <Clickable
                            href={link.url ?? undefined}
                            className={styles.link}
                            aria-current={
                                link.url === request.path ? "page" : undefined
                            }
                        >
                            {link.text}
                        </Clickable>
                    </li>
                ))}
            </ul>
        </>
    );
};
